import { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import type { Auditoria } from '../tipos';

/**
 * A agenda do agente.
 *
 * O documento guarda o QUE, o QUANDO e as INSTRUÇÕES. Quem decide se chegou a
 * hora é a Cloud Function — o navegador não tem como ser fonte de verdade sobre
 * horário: ele pode estar com o relógio errado, em outro fuso, ou simplesmente
 * fechado na hora marcada.
 */
export interface Agendamento extends Auditoria {
  id: string;
  nome: string;
  /** Id da tarefa no registro do agente. */
  tarefa: string;
  /** 0 = domingo … 6 = sábado. */
  diasDaSemana: number[];
  /** "HH:MM" em horário de Brasília. */
  horario: string;
  instrucoes: string | null;
  ativo: boolean;
  ultimaExecucaoData?: string | null;
  ultimoResultado?: string | null;
  ultimoResultadoOk?: boolean | null;
}

export type RascunhoAgendamento = Omit<
  Agendamento,
  'id' | 'criadoEm' | 'criadoPor' | 'atualizadoEm' | 'atualizadoPor'
>;

export const agendamentoVazio = (): RascunhoAgendamento => ({
  nome: '',
  tarefa: '',
  // Segunda a sexta às 8h: o começo do dia útil, que é quando a maior parte
  // deste trabalho faz sentido. Quem quiser outro, muda.
  diasDaSemana: [1, 2, 3, 4, 5],
  horario: '08:00',
  instrucoes: null,
  ativo: true,
});

export function useAgendamentos() {
  const [agendamentos, setAgendamentos] = useState<Agendamento[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(
    () =>
      onSnapshot(
        query(collection(db, 'agendamentos'), orderBy('horario')),
        (s) => {
          setAgendamentos(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Agendamento));
          setCarregando(false);
        },
        () => setCarregando(false),
      ),
    [],
  );

  return { agendamentos, carregando };
}

export async function salvarAgendamento(
  id: string | null,
  dados: RascunhoAgendamento,
  usuarioId: string,
): Promise<void> {
  const ref = id ? doc(db, 'agendamentos', id) : doc(collection(db, 'agendamentos'));
  if (id) {
    await updateDoc(ref, { ...dados, atualizadoEm: serverTimestamp(), atualizadoPor: usuarioId });
  } else {
    await setDoc(ref, {
      ...dados,
      ultimaExecucaoData: null,
      ultimoResultado: null,
      ultimoResultadoOk: null,
      criadoEm: serverTimestamp(),
      criadoPor: usuarioId,
      atualizadoEm: null,
      atualizadoPor: null,
    });
  }
}

export const removerAgendamento = (id: string) => deleteDoc(doc(db, 'agendamentos', id));

/** O que o agente sabe fazer. Vem da função para não duplicar o registro. */
export interface TarefaDoAgente {
  id: string;
  nome: string;
  descricao: string;
  executa: boolean;
  quando?: string;
}

export async function listarTarefas(): Promise<TarefaDoAgente[]> {
  const chamar = httpsCallable<Record<string, never>, { tarefas: TarefaDoAgente[] }>(
    functions,
    'listarTarefas',
  );
  return (await chamar({})).data.tarefas;
}

/** Passa pela agenda agora, sem esperar o horário. */
export async function rodarAgendaAgora(): Promise<Array<{ nome: string; situacao: string }>> {
  const chamar = httpsCallable<
    Record<string, never>,
    { relatorio: Array<{ nome: string; situacao: string }> }
  >(functions, 'rodarAgendaAgora');
  return (await chamar({})).data.relatorio;
}
